import { useEffect, useState } from 'react';
import { Receipt, Loader2 } from 'lucide-react';
import { baseApi, BaseApiError } from '../../../api/baseClient';
import { useAuth } from '../../../context/AuthContext';

// FOLIO DEL TICKET (10-oct-2026, Bramarket: «cada movimiento con el folio de SICAR/Wansoft para comprobar que el importe
// coincida con la venta real»). Por negocio (tenant_settings.loyalty_flags.ticket_ref), se guarda AL INSTANTE como
// «Pagar con saldo». Con folio, el mismo ticket no puede dar cashback dos veces en la misma sucursal (eso aplica siempre).
type Modo = 'off' | 'optional' | 'required';
const OPCIONES: { v: Modo; t: string; d: string }[] = [
  { v: 'off', t: 'No pedir', d: 'Como hasta ahora: solo el monto.' },
  { v: 'optional', t: 'Opcional', d: 'Aparece el campo; si lo capturan, se valida que no se repita.' },
  { v: 'required', t: 'Obligatorio', d: 'No se registra ninguna compra sin folio.' },
];

export default function TicketRefToggle() {
  const { patchCurrentUser } = useAuth();
  const [modo, setModo] = useState<Modo | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    baseApi.get<{ ticketRef?: Modo }>('/loyalty/settings')
      .then((d) => { if (vivo) setModo(d.ticketRef || 'off'); })
      .catch(() => { if (vivo) setModo('off'); });
    return () => { vivo = false; };
  }, []);

  const elegir = async (next: Modo) => {
    if (modo === null || saving || next === modo) return;
    setSaving(true); setError(null);
    try {
      const r = await baseApi.put<{ ticketRef: Modo }>('/loyalty/ticket-ref', { mode: next });
      setModo(r.ticketRef);
      // Sin esto el registro de visita / alta / confirmaciones seguían con el modo de cuando se inició sesión
      // (Hogar China City 24-oct: pasó a «No pedir» y le seguía exigiendo folio).
      patchCurrentUser({ ticketRef: r.ticketRef } as any);
    } catch (e) {
      setError(e instanceof BaseApiError ? e.message : 'No se pudo guardar. Intenta de nuevo.');
    } finally { setSaving(false); }
  };

  return (
    <div className="pt-4 border-t border-line" data-testid="ticket-ref-toggle">
      <div className="flex items-start gap-2">
        <Receipt className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-content flex items-center gap-2">
            Folio del ticket al registrar una compra {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          </p>
          <p className="text-xs text-content-3 mt-0.5">
            El cajero captura el folio de tu punto de venta (SICAR, Wansoft…). Así cada cashback se puede comprobar contra
            la venta real, y el mismo ticket no puede usarse dos veces en la misma sucursal.
          </p>
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {OPCIONES.map((o) => (
              <button key={o.v} type="button" onClick={() => elegir(o.v)} disabled={modo === null || saving}
                data-testid={`ticket-ref-${o.v}`} aria-pressed={modo === o.v}
                className={`rounded-lg border px-3 py-2 text-left transition disabled:opacity-60 ${modo === o.v ? 'border-green-600 bg-green-600/10' : 'border-line hover:bg-surface-2'}`}>
                <span className="block text-sm font-semibold text-content">{o.t}</span>
                <span className="block text-[11px] text-content-3">{o.d}</span>
              </button>
            ))}
          </div>
          {error && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error}</p>}
        </div>
      </div>
    </div>
  );
}
